'use client'

import * as React from 'react'
import { Search, SlidersHorizontal, X, Clock, Trash2, Filter } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui'
import { useUserStore } from '@/lib/store'
import { removeRecentSearch as removeRecentSearchFirestore, clearRecentSearches as clearRecentSearchesFirestore } from '@/lib/firebase'
import { useAuth } from '@/components/auth'
import { useI18n } from '@/lib/i18n'
import type { RecentSearch } from '@/lib/types'

interface RecentSearchesDropdownProps {
  onSelect: (search: RecentSearch) => void
  visible: boolean
  filterText?: string
  className?: string
}

const MAX_DISPLAYED = 8

export function RecentSearchesDropdown({
  onSelect,
  visible,
  filterText,
  className,
}: RecentSearchesDropdownProps) {
  const { recentSearches, removeRecentSearch, clearRecentSearches } = useUserStore()
  const { user } = useAuth()
  const { t } = useI18n()

  const isFiltering = !!filterText?.trim()

  // Filter searches by typed text (case-insensitive substring match)
  const filtered = React.useMemo(() => {
    if (!isFiltering) return recentSearches
    const needle = filterText!.trim().toLowerCase()
    return recentSearches.filter((s) =>
      s.displayLabel.toLowerCase().includes(needle)
    )
  }, [recentSearches, filterText, isFiltering])

  const displayed = filtered.slice(0, MAX_DISPLAYED)

  if (!visible || !user || recentSearches.length === 0 || (isFiltering && displayed.length === 0)) {
    return null
  }

  const handleRemove = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    removeRecentSearch(id)
    if (user) {
      removeRecentSearchFirestore(user.uid, id).catch(console.error)
    }
  }

  const handleClearAll = () => {
    clearRecentSearches()
    if (user) {
      clearRecentSearchesFirestore(user.uid).catch(console.error)
    }
  }

  return (
    <div
      className={cn(
        'absolute left-0 right-0 top-full z-50 mt-2',
        'rounded-xl bg-popover border border-border/40 shadow-lg',
        'p-3 animate-in fade-in-0 slide-in-from-top-2 duration-200',
        'max-h-[40vh] overflow-y-auto scrollbar-hide',
        className,
      )}
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          {isFiltering ? (
            <>
              <Filter className="w-3.5 h-3.5" />
              {t('search.matchingSearches', 'Matching searches')}
            </>
          ) : (
            <>
              <Clock className="w-3.5 h-3.5" />
              {t('search.recentSearches', 'Recent searches')}
            </>
          )}
        </div>
        {!isFiltering && (
          <button
            type="button"
            onClick={handleClearAll}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            {t('search.clearAll', 'Clear all')}
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {displayed.map((search) => (
          <button
            key={search.id}
            type="button"
            onClick={() => onSelect(search)}
            className="group max-w-full"
          >
            <Badge
              variant="secondary"
              className={cn(
                'text-sm font-normal px-3 py-2 sm:py-1.5 gap-1.5 cursor-pointer max-w-full',
                'hover:bg-primary/10 hover:text-primary',
                'active:bg-primary/15 active:text-primary',
                'transition-colors',
              )}
            >
              {search.type === 'text' ? (
                <Search className="w-3.5 h-3.5 shrink-0 text-muted-foreground group-hover:text-primary" />
              ) : (
                <SlidersHorizontal className="w-3.5 h-3.5 shrink-0 text-muted-foreground group-hover:text-primary" />
              )}
              <span className="truncate">{search.displayLabel}</span>
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => handleRemove(e, search.id)}
                className={cn(
                  'ml-0.5 shrink-0 rounded-full p-0.5',
                  'opacity-100 sm:opacity-0 sm:group-hover:opacity-100',
                  'hover:bg-muted-foreground/20 transition-all',
                )}
              >
                <X className="w-3 h-3" />
              </span>
            </Badge>
          </button>
        ))}
      </div>
    </div>
  )
}
